// src/utils/classDisplayUtils.ts
// Utility functions for displaying class information consistently

import { ClassAllocation } from '../types/grid';

/**
 * Format class display name with code and name
 * @param classItem The class allocation object
 * @returns Formatted string: "CODE - NAME" or just "NAME" if no code
 */
export function formatClassDisplayName(classItem: ClassAllocation): string {
  if (classItem.subjectCode && classItem.subjectCode.trim() !== '') {
    return `${classItem.subjectCode} - ${classItem.className}`;
  }
  return classItem.className;
}

/**
 * Get short class display name (code only if available, otherwise name)
 * @param classItem The class allocation object
 * @returns Short display string
 */
export function formatClassShortName(classItem: ClassAllocation): string {
  if (classItem.subjectCode && classItem.subjectCode.trim() !== '') {
    return classItem.subjectCode;
  }
  return classItem.className;
}

/**
 * Get class display name for tooltips and detailed views
 * @param classItem The class allocation object
 * @returns Detailed display string with metadata
 */
export function formatClassDetailedName(classItem: ClassAllocation): string {
  const baseName = formatClassDisplayName(classItem);
  const details: string[] = [];
  
  if (classItem.curriculumArea) {
    details.push(classItem.curriculumArea);
  }
  
  if (classItem.yearLevel) {
    details.push(`Year ${classItem.yearLevel}`);
  }
  
  if (classItem.studentCount && classItem.maxStudents) {
    details.push(`${classItem.studentCount}/${classItem.maxStudents} students`);
  }
  
  if (details.length > 0) {
    return `${baseName} (${details.join(', ')})`;
  }
  
  return baseName;
}

/**
 * Extract class code from class name if no explicit code is available
 * This is a fallback for classes without subjectCode
 * @param className The class name
 * @returns Extracted code or original name
 */
export function extractClassCode(className: string): string {
  // Look for common patterns like "DISTETIMFA - DISCOVER - STEAM - TIMBER"
  const codeMatch = className.match(/^([A-Z0-9]{6,12})\s*-/);
  if (codeMatch) {
    return codeMatch[1];
  }
  
  // Look for patterns like "12ENGENGA" at the start
  const unitCodeMatch = className.match(/^([A-Z0-9]{8,10})/);
  if (unitCodeMatch) {
    return unitCodeMatch[1];
  }
  
  // Fallback to first few words
  return className.split(' ').slice(0, 2).join(' ');
}

/**
 * Extract level from class name (e.g., Discover, Explore, Focus, etc.)
 * @param className The class name
 * @returns Level name or undefined if not found
 */
export function extractClassLevel(className: string): string | undefined {
  if (!className) return undefined;
  const upperName = className.toUpperCase();
  
  // Check for explicit level mentions in the class name
  if (upperName.includes('DISCOVER')) return 'Discover';
  if (upperName.includes('ENGAGE')) return 'Engage';
  if (upperName.includes('EXPLORE')) return 'Explore';
  if (upperName.includes('FOCUS')) return 'Focus';
  if (upperName.includes('REACH')) return 'Reach';
  if (upperName.includes('UNITS 1') || upperName.includes('UNIT 1')) return 'Units 1 & 2';
  if (upperName.includes('UNITS 3') || upperName.includes('UNIT 3')) return 'Units 3 & 4';
  if (upperName.includes('VCE') && upperName.includes('12')) return 'Units 1 & 2';
  if (upperName.includes('VCE') && upperName.includes('34')) return 'Units 3 & 4';
  if (upperName.includes('VOCATIONAL MAJOR') || upperName.includes('VM')) return 'Vocational Major';
  if (upperName.includes('ORATORY')) return 'Oratory';
  
  // Check for level codes in the class code (if it exists)
  const codeMatch = className.match(/^([A-Z0-9]{6,12})/);
  if (codeMatch) {
    const code = codeMatch[1];
    if (code.includes('DI')) return 'Discover';
    if (code.includes('EN')) return 'Engage';
    if (code.includes('EX')) return 'Explore';
    if (code.includes('FO')) return 'Focus';
    if (code.includes('RE')) return 'Reach';
    if (code.includes('12')) return 'Units 1 & 2';
    if (code.includes('34')) return 'Units 3 & 4';
    if (code.includes('ORA')) return 'Oratory';
    if (code.includes('VM')) return 'Vocational Major';
  }
  
  return undefined;
}